import { toPng } from 'html-to-image';

/**
 * PNG / PDF export of the whole chart.
 *
 * The element captured is the untransformed content layer, not the viewport, so
 * the picture is of the entire tree rather than of whatever happens to be on
 * screen — the single easiest thing to get wrong here. Its own `width`/`height`
 * style already spans the full layout, so no transform juggling is needed.
 *
 * `pixelRatio: 2` because an org chart is mostly small text; at 1x the names are
 * unreadable once the image is scaled to fit a page.
 */

const PIXEL_RATIO = 2;

const captureCanvas = (element) =>
  toPng(element, {
    pixelRatio: PIXEL_RATIO,
    backgroundColor: '#f8fafc',
    // Fonts and avatars are inlined by html-to-image; skip anything that would
    // taint the canvas and fail the whole export.
    filter: (node) => node.tagName !== 'IFRAME',
  });

export const exportChartPng = async (element, filename = 'org-chart') => {
  if (!element) throw new Error('Nothing to export yet.');

  const dataUrl = await captureCanvas(element);
  const link = document.createElement('a');
  link.download = `${filename}.png`;
  link.href = dataUrl;
  link.click();
};

export const exportChartPdf = async (element, filename = 'org-chart') => {
  if (!element) throw new Error('Nothing to export yet.');

  const dataUrl = await captureCanvas(element);

  // jsPDF is a heavy import and only this button needs it; loading it on demand
  // keeps it out of the main bundle for everyone who never exports.
  const { jsPDF } = await import('jspdf');

  const width = element.offsetWidth;
  const height = element.offsetHeight;
  const landscape = width >= height;

  const pdf = new jsPDF({
    orientation: landscape ? 'landscape' : 'portrait',
    unit: 'pt',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 24;

  // Fit the whole chart on one page; a tall tree would otherwise be cropped.
  const scale = Math.min((pageWidth - margin * 2) / width, (pageHeight - margin * 2) / height);
  const drawWidth = width * scale;
  const drawHeight = height * scale;

  pdf.addImage(
    dataUrl,
    'PNG',
    (pageWidth - drawWidth) / 2,
    (pageHeight - drawHeight) / 2,
    drawWidth,
    drawHeight,
  );
  pdf.save(`${filename}.pdf`);
};
